import React, { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  StickerStar, StickerBolt, StickerSun, StickerHeart, StickerPeace,
  StickerSunflower, StickerCampfire, StickerWave,
} from "../components/Stickers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Speaking = () => {
  const [form, setForm] = useState({
    name: "", email: "", organization: "", event_type: "",
    event_date: "", location: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email, and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setSent(true);
      setForm({ name: "", email: "", organization: "", event_type: "", event_date: "", location: "", message: "" });
      toast.success("Request sent. I'll reach out soon.");
    } catch (err) {
      toast.error("Couldn't send. Try emailing me directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Toaster position="top-center" richColors />
      <section className="bg-[#F4A261] border-b-4 border-black relative overflow-hidden">
        <div className="absolute top-6 left-6 hidden md:block"><StickerStar size={75} rotate={-12} className="float-slow" /></div>
        <div className="absolute bottom-6 right-6 hidden md:block"><StickerBolt size={80} rotate={10} className="float-med" /></div>

        <div className="max-w-7xl mx-auto py-20 px-6 text-white">
          <div className="font-pixel uppercase tracking-widest text-lg text-white/80">▒▒ Public speaking ▒▒</div>
          <h1 className="font-display tracking-wider text-6xl sm:text-7xl lg:text-8xl mt-3">BOOK ME</h1>
          <p className="font-body text-xl mt-5 max-w-3xl">
            Schools · fundraisers · corporate events · community gatherings. I tell the real story of running 7,000 km across Canada
            and what it actually takes to chase something this big. 5,000+ kids and counting have heard it in person.
          </p>
        </div>
      </section>

      <section className="py-20 px-6 relative">
        <div className="absolute top-10 right-12 hidden md:block"><StickerSun size={70} rotate={14} className="float-med" /></div>
        <div className="absolute bottom-10 left-12 hidden md:block"><StickerPeace size={80} rotate={-10} className="float-slow" /></div>
        <div className="absolute top-1/2 right-2 hidden xl:block"><StickerCampfire size={80} rotate={-10} className="float-med" /></div>
        <div className="absolute bottom-1/3 left-2 hidden xl:block"><StickerSunflower size={80} rotate={12} className="float-slow" /></div>

        <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-10">
          {/* What you get */}
          <aside className="lg:col-span-2 space-y-6">
            <div className="bg-white border-4 border-black shadow-[8px_8px_0_#000] p-6">
              <h3 className="font-display tracking-wider text-3xl text-[#F87171]">WHAT TO EXPECT</h3>
              <ul className="font-body text-lg mt-3 space-y-2 list-disc list-inside">
                <li>30–60 min keynote, customizable by age group</li>
                <li>Q&A with students or audience</li>
                <li>Photo & autograph time</li>
                <li>Book copies available on request</li>
              </ul>
            </div>

            <div className="bg-[#F4D58D] border-4 border-black shadow-[8px_8px_0_#000] p-6">
              <h3 className="font-display tracking-wider text-3xl text-black">PAST AUDIENCES</h3>
              <ul className="font-body text-lg mt-3 space-y-1">
                <li>★ Elementary &amp; high schools (K–12)</li>
                <li>★ University clubs and athletic teams</li>
                <li>★ Cancer fundraisers &amp; community runs</li>
                <li>★ Corporate &amp; team-building events</li>
              </ul>
            </div>

            <div className="bg-[#0A0A0A] text-white border-4 border-black shadow-[8px_8px_0_#F87171] p-6">
              <h3 className="font-display tracking-wider text-3xl text-[#8ECAE6]">PREFER EMAIL?</h3>
              <p className="font-body text-base mt-2">Skip the form, send a note directly:</p>
              <a href="mailto:CrossCanadaJax97@gmail.com?subject=Speaking%20inquiry" data-testid="speaking-direct-email"
                className="inline-block mt-3 font-display tracking-wider text-2xl px-5 py-2 bg-[#F4D58D] text-black border-4 border-white shadow-[4px_4px_0_#fff] btn-press">
                CrossCanadaJax97@gmail.com
              </a>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-3">
            {sent ? (
              <div className="bg-white border-4 border-black shadow-[8px_8px_0_#000] p-8 text-center relative" data-testid="speaking-success">
                <div className="absolute -top-8 -right-8"><StickerHeart size={90} rotate={14} /></div>
                <div className="font-display tracking-wider text-5xl text-[#F87171]">REQUEST SENT</div>
                <p className="font-body text-lg mt-4">
                  Thanks for reaching out! I'll get back to you at the email you provided within a few days.
                </p>
                <button onClick={() => setSent(false)} data-testid="speaking-send-another"
                  className="mt-6 font-display text-2xl tracking-wider px-6 py-2 bg-[#F4D58D] border-4 border-black shadow-[4px_4px_0_#000] btn-press">
                  Send another →
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white border-4 border-black shadow-[10px_10px_0_#000] p-6 sm:p-8 space-y-4" data-testid="speaking-form">
                <h3 className="font-display tracking-wider text-4xl text-black">REQUEST A TALK</h3>
                <p className="font-pixel text-xl text-black/70">Fill it in. I read every one.</p>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Your name *">
                    <input required type="text" value={form.name} onChange={update("name")}
                      className="w-full font-pixel text-xl bg-[#FFF4D6] border-4 border-black px-4 py-3 outline-none focus:bg-[#FFFEC2]"
                      data-testid="speaking-name" />
                  </Field>
                  <Field label="Email *">
                    <input required type="email" value={form.email} onChange={update("email")}
                      className="w-full font-pixel text-xl bg-[#FFF4D6] border-4 border-black px-4 py-3 outline-none focus:bg-[#FFFEC2]"
                      data-testid="speaking-email" />
                  </Field>
                  <Field label="School / Organization">
                    <input type="text" value={form.organization} onChange={update("organization")}
                      className="w-full font-pixel text-xl bg-[#FFF4D6] border-4 border-black px-4 py-3 outline-none focus:bg-[#FFFEC2]"
                      data-testid="speaking-org" />
                  </Field>
                  <Field label="Event type">
                    <select value={form.event_type} onChange={update("event_type")}
                      className="w-full font-pixel text-xl bg-[#FFF4D6] border-4 border-black px-4 py-3 outline-none"
                      data-testid="speaking-event-type">
                      <option value="">choose one...</option>
                      <option>School assembly</option>
                      <option>Classroom visit</option>
                      <option>Fundraiser</option>
                      <option>Corporate event</option>
                      <option>Community / club</option>
                      <option>Other</option>
                    </select>
                  </Field>
                  <Field label="Event date (approx.)">
                    <input type="text" placeholder="e.g. May 14, 2026" value={form.event_date} onChange={update("event_date")}
                      className="w-full font-pixel text-xl bg-[#FFF4D6] border-4 border-black px-4 py-3 outline-none focus:bg-[#FFFEC2]"
                      data-testid="speaking-event-date" />
                  </Field>
                  <Field label="City / location">
                    <input type="text" value={form.location} onChange={update("location")}
                      className="w-full font-pixel text-xl bg-[#FFF4D6] border-4 border-black px-4 py-3 outline-none focus:bg-[#FFFEC2]"
                      data-testid="speaking-location" />
                  </Field>
                </div>

                <Field label="Tell me about your event *">
                  <textarea required rows={5} value={form.message} onChange={update("message")}
                    className="w-full font-pixel text-xl bg-[#FFF4D6] border-4 border-black px-4 py-3 outline-none resize-none focus:bg-[#FFFEC2]"
                    placeholder="Audience size, age group, theme, anything I should know..."
                    data-testid="speaking-message" />
                </Field>

                <button type="submit" disabled={submitting} data-testid="speaking-submit"
                  className="w-full font-display tracking-wider text-3xl uppercase px-6 py-4 bg-[#F87171] text-white border-4 border-black shadow-[8px_8px_0_#000] btn-press disabled:opacity-60">
                  {submitting ? "Sending..." : "Send Booking Request →"}
                </button>
                <p className="font-pixel text-base text-black/60 text-center">
                  Or email me at <a href="mailto:CrossCanadaJax97@gmail.com" className="retro-link">CrossCanadaJax97@gmail.com</a>
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-pixel text-lg uppercase tracking-wider text-black/70 block mb-1">{label}</span>
    {children}
  </label>
);

export default Speaking;
